const textEncode = (value) => {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value);
  }
  
  const arr = new Uint8Array(value.length);
  for (let i = 0; i < value.length; i++) {
    arr[i] = value.charCodeAt(i);
  }
  return arr;
};

const encodeBase64 = (value) => {
  if (typeof btoa === 'function') {
    return btoa(value);
  }
  
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(value, 'binary').toString('base64');
  }
  
  throw new Error('Base64 encoding is not supported in this environment.');
};

const decodeBase64 = (base64) => {
  if (typeof atob === 'function') {
    return atob(base64);
  }
  
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(base64, 'base64').toString('binary');
  }
  
  throw new Error('Base64 decoding is not supported in this environment.');
};

const bufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return encodeBase64(binary);
};

const base64ToBuffer = (base64) => {
  const binaryString = decodeBase64(base64);
  
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

const getCrypto = () => {
  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    return window.crypto;
  }
  return null;
};

const deriveKey = async (secret) => {
  const crypto = getCrypto();
  if (!crypto) return null;
  const secretData = textEncode(secret);
  const hashBuffer = await crypto.subtle.digest('SHA-256', secretData);
  return crypto.subtle.importKey(
    'raw',
    hashBuffer,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
};

export const encryptText = async (secret, plainText) => {
  if (!plainText) {
    return null;
  }
  
  const crypto = getCrypto();
  if (!secret || !crypto) {
    return {
      cipher: encodeBase64(plainText),
      algorithm: 'base64',
    };
  }
  
  const key = await deriveKey(secret);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encryptedBuffer = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    key,
    textEncode(plainText),
  );
  
  return {
    cipher: bufferToBase64(encryptedBuffer),
    iv: bufferToBase64(iv.buffer),
    algorithm: 'AES-GCM',
  };
};

export const decryptText = async (secret, payload) => {
  if (!payload) {
    return null;
  }
  
  if (payload.algorithm === 'base64' || !payload.algorithm) {
    return decodeBase64(payload.cipher);
  }
  
  const crypto = getCrypto();
  if (!secret || !crypto) {
    return null;
  }
  
  const key = await deriveKey(secret);
  const ivBuffer = base64ToBuffer(payload.iv);
  const cipherBuffer = base64ToBuffer(payload.cipher);
  
  const decrypted = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: new Uint8Array(ivBuffer),
    },
    key,
    cipherBuffer,
  );
  
  const bytes = new Uint8Array(decrypted);
  let result = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    result += String.fromCharCode(bytes[i]);
  }
  return result;
};


